import { useEffect, useRef, useState, useCallback } from 'react';
import quantService from '../../../../services/quantService';

// Polls /api/quant/discovery/<jobId>/ or /api/quant/backtest/<jobId>/ every
// `intervalMs` ms (default 1.5s) until the job reaches a terminal status.
// Stops polling on done | killed | failed | cancelled | interrupted_resumable.
//
// type: 'discovery' | 'backtest'
//
export default function useQuantJob(type, jobId, intervalMs = 1500) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const fetchOnce = useCallback(async () => {
    if (!jobId) return;
    try {
      const fetcher = type === 'backtest'
        ? quantService.getBacktest
        : quantService.getDiscovery;
      const { data } = await fetcher(jobId);
      setJob(data);
      setError(null);
      setLoading(false);
      // Stop polling if terminal
      if (['done', 'killed', 'failed', 'cancelled', 'interrupted_resumable'].includes(data.status)) {
        if (timer.current) {
          clearInterval(timer.current);
          timer.current = null;
        }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setLoading(false);
    }
  }, [type, jobId]);

  useEffect(() => {
    fetchOnce();
    timer.current = setInterval(fetchOnce, intervalMs);
    return () => timer.current && clearInterval(timer.current);
  }, [fetchOnce, intervalMs]);

  return { job, loading, error, refresh: fetchOnce };
}
